import { Injectable, NgZone } from '@angular/core';
import { RolesEnum } from 'src/app/shared/enums/roles.enum';
import { AccountService } from '../auth/account.service';
import { TabService } from '../tab.service';
import { TimeRestrictionService } from '../restrictions/time-restriction.service';
import { ModalWindowService } from '../ui/modal-window.service';
import { MatDialog } from '@angular/material/dialog';
import { ExamInformationDialogComponent, IExamInformation } from 'src/app/components/dialogs/exam-information-dialog/exam-information-dialog.component';
import { AsgardFeatureNames } from 'src/models/AsgardFeatures';
import { CurrentUiStoreService } from '../ui/current-ui-store.service';
import { MessageDialogActionColor, MessageDialogComponent, MessageDialogData } from 'src/app/components/dialogs/message-dialog/message-dialog.component';
import { ExamUnlockDialogComponent, ExamUnlockDialogData, IExamUnlockDialogResult } from 'src/app/components/dialogs/exam-unlock-dialog/exam-unlock-dialog.component';
import { BehaviorSubject, fromEvent, Observable, Subscription } from 'rxjs';
import { ExamTimerService } from '../restrictions/exam-timer.service';
import { PaneService } from '../pane.service';
import { YesNoDialogComponent } from 'src/app/components/dialogs/yes-no-dialog/yes-no-dialog.component';
import { FeatureRestrictionService } from '../restrictions/feature-restriction.service';
import { ExamPropertiesComponent, ExamPropertiesOptions, ExamPropertiesType } from 'src/app/components/examMode/exam-properties/exam-properties.component';
import { CurrentTimeService } from '../restrictions/current-time.service';
import { FormGroup } from '@angular/forms';
import { MimeTypesEnum, ToastrActionEnum } from 'src/app/utils/enums';
import { DocumentManagerService } from '../document/document-manager.service';
import { SettingsService } from '../settings/settings.service';
import { Exam } from 'src/app/services/exam/interfaces/exam.interface';
import { ExamDocumentType } from 'src/app/services/exam/enums/document-type.enum';
import { CurrentExamService } from './current-exam.service';
import { AuthService } from '../auth/auth.service';
import { AutosaveManagerService } from '../autosave/autosave-manager.service';
import { ReportLogLevel } from './enums/report-level.enum';
import { ReportExamService } from './report-exam.service';
import { IAutoSaveDoc } from '../autosave/autosave.interface';
import { FileRestoringService } from '../file-restoring.service';
import { FileConvertorService } from '../file-convertor/file-convertor-service';
import { RtfToHtmlConversion } from '../file-convertor/conversions';
import { FileHashManager } from '../autosave/file-hash-manager';
import { ExamCommunicationService } from './exam-communication.service';
import { ExtraDocumentsRestrictionService } from '../restrictions/extra-documents-restriction.service';
import { TimeRestriction } from '../restrictions/models/time-restriction.model';
import { ExamPropertiesTabNames } from './enums/properties-tab-names';
import { ToastrDialogService } from '../toastr-dialog.service';
import { TranslateService } from '@ngx-translate/core';
import { PdfInstanceManagerService } from '../instance/pdf-instance-manager.service';
import { ExamService } from '../restrictions/exam.service';
import { CurrentUIState } from 'src/models/UIState';
import { environment } from 'src/environments/environment';
import { IExamModel } from 'src/app/components/examMode/interfaces';
// import { TranslateService } from '@ngx-translate/core';
@Injectable({
  providedIn: 'root'
})
export class ExamModeRestrictionsService {
  private subscriptions: Subscription = new Subscription();

  private examCreationIsActive = new BehaviorSubject(false);

  private filePickerIsOpen = false;
  private testMode = false;
  private formGroup: FormGroup = new FormGroup({});

  private activeExam: Exam | undefined = undefined;
  private examInProgress: boolean = false;
  private activeTabWhileTesting: ExamPropertiesTabNames | undefined;
  private testingExamTabId = '';
  private examTabId = '';

  // REFACTOR IDEA, when services are cleaned up, this will no longer be needed
  private examPropertiesType: ExamPropertiesType | undefined;
  private prepExam: IExamModel | undefined;

  private testmodeFeatures: AsgardFeatureNames[] = [];
  private featuresReportSub: Subscription | null = null;

  offlineEvent: Observable<Event>;
  onlineEvent: Observable<Event>;
  windowSubscriptions: Subscription[] = [];

  get examPropertiesFormGroup(): FormGroup {
    return this.formGroup;
  }

  get isExamInProgress$(): Observable<boolean> {
    return this.currentExamService.inProgressObservable;
  }

  get isTestModeActive(): boolean {
    return this.testMode;
  }

  get isExamCreationActive(): boolean {
    return this.examCreationIsActive.value;
  }

  get isExamInProgress(): boolean {
    return this.currentExamService.inProgress;
  }

  get notAllowedToCloseDocumentTab(): boolean {
    return this.isExamCreationActive
      && (this.documentManager.getAllDocuments(MimeTypesEnum.ApplicationPdf).length > 0
        || this.formGroup.dirty
      );
  }

  get notAllowedToCloseBrowserTab(): boolean {
    return this.isExamCreationActive
      && this.documentManager.getAllDocuments(MimeTypesEnum.ApplicationPdf).length > 0
      || this.formGroup.dirty;
  }

  constructor(
    private readonly accountService: AccountService,
    private readonly tabService: TabService,
    private readonly timeRestrictionsService: TimeRestrictionService,
    private readonly featureRestrictionService: FeatureRestrictionService,
    private readonly modalService: ModalWindowService,
    private readonly dialog: MatDialog,
    private readonly uiStoreService: CurrentUiStoreService,
    private readonly timerService: ExamTimerService,
    private readonly paneService: PaneService,
    private readonly currentTimeService: CurrentTimeService,
    private readonly documentManager: DocumentManagerService,
    private readonly settingsService: SettingsService,
    private readonly examTimeService: ExamTimerService,
    private readonly currentExamService: CurrentExamService,
    private readonly ngZone: NgZone,
    private readonly authService: AuthService,
    private readonly autoSave: AutosaveManagerService,
    private readonly reportExamService: ReportExamService,
    private readonly fileRestoringService: FileRestoringService,
    private readonly fileConvertor: FileConvertorService,
    private readonly examCommunicationService: ExamCommunicationService,
    private readonly extraDocumentsRestrictionService: ExtraDocumentsRestrictionService,
    private readonly toastrService: ToastrDialogService,
    private readonly translate: TranslateService,
    private readonly pdfInstanceManager: PdfInstanceManagerService,
    private readonly examService: ExamService,
    private readonly uiService: CurrentUiStoreService
  ) {
    this.initSunscriptions();
  }

  async isSupervisor(): Promise<boolean> {
    const account = await this.accountService.getAccountInfo();
    if (account && account.roles.includes(RolesEnum.Supervisor)) {
      return true;
    }
    return false;
  }

  async isUser(): Promise<boolean> {
    const account = await this.accountService.getAccountInfo();
    if (account && account.roles.length === 1 && account.roles[0] === RolesEnum.User) {
      return true;
    }
    return false;
  }

  async openExam(examDoc: IAutoSaveDoc | File, drafts: IAutoSaveDoc[] | File[] | undefined = undefined) {
    await this.closeAllDocuments();
    this.setSinglePane();

    try {
      let draftTabId: string = '';

      this.ngZone.run(async () => {
        this.examTabId = examDoc instanceof File ? await this.tabService.setTabFile(examDoc as File)
          : await this.fileRestoringService.restore(examDoc as IAutoSaveDoc);

        this.currentExamService.setExamTabId(this.examTabId)

        if (drafts && drafts.length > 0) {
          for (let i = 0; i < drafts.length; i++) {
            if (drafts[i] instanceof File) {
              // draftTabId = await this.tabService.setTabFile(drafts[i] as File);
              console.log('drafts[i]', drafts[i]);
              const file = drafts[i] as File;

              const fileHash = await FileHashManager.calculateFileHash(file);
              await this.fileConvertor.silentMakeConversion<RtfToHtmlConversion>(RtfToHtmlConversion, {
                file,
                fileHash
              });
            } else {
              // const tabName = `${this.translate.instant('COMMON.DRAFT')}${i + 1}`;
              // draftTabId = await this.fileRestoringService.restore(drafts[i] as IAutoSaveDoc, tabName);
              draftTabId = await this.fileRestoringService.restore(drafts[i] as IAutoSaveDoc);
            }

            this.currentExamService.addDraftTabId(draftTabId);
          }

          this.tabService.selectTab(this.examTabId);
        }
      });
    } catch (error) {
      console.log('Unable to open exam. Reason:', error);
    }
  }

  async testExam(activeTab: ExamPropertiesTabNames, examPropertiesType: ExamPropertiesType, prepExam: IExamModel): Promise<void> {
    this.activeTabWhileTesting = activeTab;
    this.examPropertiesType = examPropertiesType;
    this.prepExam = prepExam;

    this.testMode = true;
    this.modalService.closeModal();
    this.startTest();
  }

  handleTimer() {
    if (document.fullscreenElement) {
      this.currentTimeService.start();
    } else {
      this.currentTimeService.stop();
    }
  }

  stopTest(): void {
    this.removeRestrictions();
    // close the tab with the exam file
    this.tabService.closeTab(this.testingExamTabId);
    this.testingExamTabId = '';

    // unhide the other tabs
    const tabs = this.tabService.allTabs;
    tabs.forEach(tab => tab.isHidden = false)

    this.cancel();
    this.testMode = false;
    this.currentExamService.setInProgress(false);

    // REFACTOR IDEA, after services are simplified, this options don't need to be here like this!
    // if examPropertiesType is undefined, ERROR
    if (this.examPropertiesType === undefined) {
      console.error('stop test, but examPropertiesType is undefined, so open in read-only mode');
    }
    const options: ExamPropertiesOptions = {
      currentTab: this.activeTabWhileTesting,
      exam: this.prepExam,
      type: this.examPropertiesType ?? ExamPropertiesType.readOnly,
    }
    this.modalService.createModalWindow(ExamPropertiesComponent, options);
    this.activeTabWhileTesting = undefined;
    this.prepExam = undefined;
    this.examPropertiesType = undefined

    // show the floating toolbar again
    this.uiStoreService.setItemActive(AsgardFeatureNames.EXAM_CREATION_FLOATING_TOOLBAR);
  }

  toggleFilePickerIsOpen(value: boolean): void {
    this.filePickerIsOpen = value;
  }

  private handleAppConnectivityChanges(): void {
    this.onlineEvent = fromEvent(window, 'online');
    this.offlineEvent = fromEvent(window, 'offline');

    this.windowSubscriptions.push(this.onlineEvent.subscribe(() => {
      // handle online mode
      if (this.isExamInProgress && !this.dialog.getDialogById('exam-unlock') && !this.testMode) {
        this.reportExamService.sendTemplateMessageReport(ReportLogLevel.Interruptions, 'WOLAND.REPORT_TEMPLATES.INTERNET_RECOVERED');
        this.lockExam(this.translate.instant('WOLAND.REPORT_TEMPLATES.INTERNET_RECOVERED'));
        console.log('Online...');
      }
    }));

    this.windowSubscriptions.push(this.offlineEvent.subscribe(() => {
      // handle offline mode
      console.log('Offline...');
    }));
  }

  handleFullscreenChange(): void {
    if (this.isExamInProgress && !this.dialog.getDialogById('exam-unlock') && !document.fullscreenElement && !this.filePickerIsOpen && !this.testMode) {
      this.reportExamService.sendTemplateMessageReport(ReportLogLevel.Interruptions, 'WOLAND.REPORT_TEMPLATES.EXIT_KIOSK_MODE_TEMPLATE');
      this.lockExam();
    }
  }

  handleVisibilityChange(): void {
    if (this.isExamInProgress && !this.dialog.getDialogById('exam-unlock') && document.visibilityState === 'hidden' && !this.testMode) {
      this.reportExamService.sendTemplateMessageReport(ReportLogLevel.Interruptions, 'WOLAND.REPORT_TEMPLATES.EXIT_KIOSK_MODE_TEMPLATE');
      this.lockExam();
    }
  }

  handleBlurEvent(): void {
    if (this.isExamInProgress && !this.dialog.getDialogById('exam-unlock') && !this.testMode) {
      this.reportExamService.sendTemplateMessageReport(ReportLogLevel.Interruptions, 'WOLAND.REPORT_TEMPLATES.EXIT_KIOSK_MODE_TEMPLATE');
      this.lockExam();
    }
  }

  handleFocusEvent(): void {
    if (this.filePickerIsOpen) {
      this.toggleFilePickerIsOpen(false);
      if (this.isExamInProgress) {
        this.goFullscreen();
      }
    }
  }

  terminateExamIfNeeded(): Promise<void> {
    if (!this.isExamInProgress) {
      return Promise.resolve();
    }

    return new Promise<void>((resolve, reject) => {
      const data: MessageDialogData = {
        title: 'EXAM_TERMINATION_DIALOG.TITLE',
        message: 'EXAM_TERMINATION_DIALOG.MESSAGE',
        actions: [
          {
            title: 'EXAM_TERMINATION_DIALOG.ACTIONS.TERMINATE',
            color: MessageDialogActionColor.Primary,
            handler: () => {
              this.stopExam();
              resolve();
            }
          },
          {
            title: 'EXAM_TERMINATION_DIALOG.ACTIONS.CANCEL',
            color: MessageDialogActionColor.None,
            handler: () => reject('canceled')
          }
        ]
      };
      this.dialog.open(MessageDialogComponent, { data });
    });
  }

  async closeExam(): Promise<void> {
    return new Promise((resolve, reject) => {
      const yesHandler = () => {
        this.stopExam();
        resolve();
      };
      const noHandler = () => {
        reject();
      };
      this.modalService.createModalWindow(YesNoDialogComponent, {
        messageKey: 'EXAM_CLOSE_DIALOG.MESSAGE', yesHandler, noHandler
      });
    });
  }

  stopExam(): void {
    this.cancelExam();
    this.currentExamService.clear();
    this.examCommunicationService.dispose();
    if (this.featuresReportSub) {
      this.featuresReportSub.unsubscribe();
      this.featuresReportSub = null;
    }
  }

  pauseExamClicked(): void {
    this.lockExam();
  }

  async warnSupervisorAboutClosingDocuments(): Promise<void> {
    this.toastrService.showToaster(ToastrActionEnum.Error, this.translate.instant('EXAM_SUPERVISOR_OPEN_WARNING_DIALOG.MESSAGE'));
    // const data: MessageDialogData = {
    //   title: '', // 'EXAM_SUPERVISOR_OPEN_WARNING_DIALOG.TITLE',
    //   message: 'EXAM_SUPERVISOR_OPEN_WARNING_DIALOG.MESSAGE',
    //   actions: [
    //     {
    //       title: 'EXAM_SUPERVISOR_OPEN_WARNING_DIALOG.OK',
    //       color: MessageDialogActionColor.Primary,
    //       handler: () => { }
    //     }
    //   ]
    // };

    // await this.warnUser(data);
  }

  async warnUsersExamWillStartSoon(examName: string, examStartDelay: string): Promise<void> {
    const data: MessageDialogData = {
      title: '',
      message: 'EXAM_STUDENT_EXAM_WILL_START_SOON',
      messageParams: {
        examName,
        examStartDelay
      },
      actions: [
        {
          title: 'COMMON.OK',
          color: MessageDialogActionColor.Primary,
          handler: () => { }
        }
      ]
    };

    return this.warnUser(data);
  }

  async warnUnlockCodeChange(examName: string): Promise<void> {
    const data: MessageDialogData = {
      title: '',
      message: 'EXAM_SUPERVISOR_UNLOCK_CHANGE',
      messageParams: {
        examName
      },
      actions: [
        {
          title: 'COMMON.OK',
          color: MessageDialogActionColor.Primary,
          handler: () => { }
        }
      ]
    };

    return this.warnUser(data);
  }

  async warnUsersExamStarted(examName: string): Promise<void> {
    const data: MessageDialogData = {
      title: '',
      message: 'EXAM_STUDENT_EXAM_STARTED',
      messageParams: {
        examName
      },
      actions: [
        {
          title: 'COMMON.OK',
          color: MessageDialogActionColor.Primary,
          handler: () => { }
        }
      ]
    };

    return this.warnUser(data);
  }

  public async warnUserExamTimeIsOver(): Promise<void> {
    const data: MessageDialogData = {
      title: '',
      message: 'EXAM_STUDENT_EXAM_TIME_IS_OVER',
      actions: [
        {
          title: 'COMMON.OK',
          color: MessageDialogActionColor.Primary,
          handler: () => { }
        }
      ]
    };

    return this.warnUser(data);
  }

  public async warnUserExamSubmitted(): Promise<void> {
    const data: MessageDialogData = {
      title: '',
      message: 'EXAM_STUDENT_EXAM_HAS_BEEN_SUBMITTED',
      actions: [
        {
          title: 'COMMON.OK',
          color: MessageDialogActionColor.Primary,
          handler: () => { }
        }
      ]
    };

    return this.warnUser(data);
  }

  private async warnUser(data: MessageDialogData): Promise<void> {
    this.dialog.getDialogById('exam-unlock')?.close();
    return this.ngZone.run(() => this.dialog.open(MessageDialogComponent, { data }).afterClosed().toPromise());
  }

  async warnUserAboutClosingDocuments(): Promise<void> {
    const data: MessageDialogData = {
      title: '', // 'EXAM_USER_OPEN_WARNING_DIALOG.TITLE',
      message: 'EXAM_USER_OPEN_WARNING_DIALOG.MESSAGE',
      actions: [
        {
          title: 'EXAM_USER_OPEN_WARNING_DIALOG.OK',
          color: MessageDialogActionColor.Primary,
          handler: () => { }
        }
      ]
    };
    return this.dialog.open(MessageDialogComponent, { data }).afterClosed().toPromise();
    // TODO add toaster ?
  }

  activateExamCreation(): void {
    this.settingsService.backupSettings();
    this.formGroup = new FormGroup({});
    this.examCreationIsActive.next(true);

    // close the main menu
    this.closeMainMenu();
  }

  deActivateExamCreation(): void {
    this.formGroup = new FormGroup({});
    this.examCreationIsActive.next(false);
  }

  public showExamInformation(exam: Exam): Promise<void> {
    const { examDate, isAllowDraft, isStoreDraft, featureRestrictions, examFiles, name, subject } = exam;
    const { displayTimeLeft, duration, endDate } = examDate;
    const timeOptions: Intl.DateTimeFormatOptions = { hour: '2-digit', minute: '2-digit', hour12: false };

    const examInformation: IExamInformation = {
      examName: name,
      subject: subject,
      time: {
        // startTime: new Date(StartDate),
        endTime: endDate ? new Date(endDate).toLocaleTimeString([], timeOptions).slice(0, 5) : '',
        availableTime: duration ?
          this.examTimeService.convertTimeToHMString(this.examTimeService.getHoursAndMinutes(duration * 60 * 1000)) : '', //to mili
        showAvailableTime: displayTimeLeft
      },
      attachments: examFiles.filter(x => x.documentType === ExamDocumentType.Attachment).map(x => x.name),
      drafts: {
        allowed: isAllowDraft,
        included: isStoreDraft
      },
      availableFeatures: this.featureRestrictionService.getAvailableFeatures(featureRestrictions.features.concat(this.featureRestrictionService.defaultBlockedFeatures)),
      goToFullscreenHandler: async () => { await this.goFullscreen(); }
    };

    // this.examInformationDialog = this.dialog.open(ExamInformationDialogComponent, { data: examInformation });
    return this.ngZone.run(() => this.dialog.open(ExamInformationDialogComponent, { data: examInformation, disableClose: true }).afterClosed().toPromise());
  }

  private async closeAllDocuments(): Promise<void> {
    // await this.warnAboutClosingDocuments();
    this.tabService.closeAllTabs();
  }

  public async startExam(asInterrupted: boolean = false): Promise<void> {
    this.modalService.closeModal();
    if (asInterrupted) {
      // when interrupted, show lock screen. when clicking continue, full screen is activated
      // when not interrupted, full screen is activated after the "exam information dialog"
      await this.ngZone.run(() => this.lockExam.call(this));
    }
    await this.start(asInterrupted);
  }

  private async startTest(): Promise<void> {
    // this.takingExamisActive$.next(true);
    this.currentExamService.setInProgress(true);
    await this.goFullscreen();

    // hide all the open tabs
    const tabs = this.tabService.allTabs;
    tabs.forEach(tab => tab.isHidden = true);

    // open the exam File
    if (this.extraDocumentsRestrictionService.examFile) {
      this.testingExamTabId = await this.tabService.setTabFile(this.extraDocumentsRestrictionService.examFile);
    }

    // start the test
    await this.start(false);
  }

  private async start(asInterrupted: boolean): Promise<void> {
    if (this.activeExam === undefined && !this.testMode) {
      throw new Error("Error during starting exam");
    }

    this.reportExamService.sendCustomMessageReport(ReportLogLevel.Information, JSON.stringify({
      version: environment.version,
      build: environment.build
    }));
    
    // TODO when WOLAND is created, apply correct way    

    if (this.examInProgress && !!this.activeExam) {
      this.setExamTimeRestriction(this.activeExam);
    }

    let duration = 0;

    if (this.testMode) {
      let timeRes = this.timeRestrictionsService.getTimeRestrictions()[0];

      duration = timeRes ? timeRes.duration : 0; 
      duration = duration * 60 * 1000;
      this.timerService.start(duration);
    } else {
      const { endDate, duration: examDruation } = this.activeExam?.examDate || { endDate: '', duration: 0 };

      duration = endDate ? this.getDurationFromEndDate(endDate) : (examDruation ? examDruation : 0) * 60 * 1000;
      this.timerService.start(duration);

      // monitor feature changes
      this.monitorFeaturesDuringExam();
    }
    
    // start the time service that shows the current system time
    this.currentTimeService.start();

    this.applyRestrictions(this.activeExam?.featureRestrictions.features.concat(this.featureRestrictionService.defaultBlockedFeatures));
    this.uiStoreService.setItemActive(AsgardFeatureNames.EXAM_MODE_FLOATING_TOOLBAR);

    if (!asInterrupted) {
      await this.firstSaveExam();
    }
  }

  private getDurationFromEndDate(endDate: string) {
    return Math.ceil((new Date(endDate).getTime() - Date.now()) / 1000) * 1000;
  }

  private setExamTimeRestriction(exam: Exam) {
    this.timeRestrictionsService.reset();

    const { startFromDate, startUntilDate, endDate, duration } = exam.examDate;

    const timeRestriction = new TimeRestriction(startFromDate, startUntilDate, endDate, duration);
    this.timeRestrictionsService.addTimeRestriction(timeRestriction);
  }

  async goFullscreen(): Promise<void> {
    if (document.fullscreenElement) {
      return Promise.resolve();
    }

    const docElement = document.documentElement as any;

    if (docElement.requestFullscreen) {
      return docElement.requestFullscreen();
    } else if (docElement.mozRequestFullScreen) {
      /* Firefox */
      return docElement.mozRequestFullScreen();
    } else if (docElement.webkitRequestFullscreen) {
      /* Chrome, Safari and Opera */
      return docElement.webkitRequestFullscreen();
    } else if (docElement.msRequestFullscreen) {
      /* IE/Edge */
      return docElement.msRequestFullscreen();
    }

    return Promise.reject('Unable to display element fullscreen');
  }

  private async exitFullscreen(): Promise<void> {
    if (document.fullscreenElement) {
      return document.exitFullscreen();
    }
  }

  private async lockExam(reason = ''): Promise<void> {
    // close all the other dialogs first
    this.closeAllDialogs();

    const data: ExamUnlockDialogData = {
      goToFullscreenHandler: async () => {
        await this.goFullscreen();
      },
      unlockReason : reason,
      testMode: this.testMode,
    };

    const result: IExamUnlockDialogResult = await this.dialog.open(
      ExamUnlockDialogComponent,
      { id: 'exam-unlock', disableClose: this.testMode ? false : true, data }
    )
      .afterClosed()
      .toPromise();

    if (result) {
      if (result.continue) {
        // continue the exam
        console.log('Unlocking exam...');
        // this.goFullscreen();

        // if extendTime is not 0 or null
        if (result.extendTime && result.extendTime > 0) {
          this.timerService.extend(result.extendTime);
        }
      } else {
        this.closeAllDialogs();
        // if it was the student taking the exam, submit the exam
        // if it was the supervisor testing the exam, stop testing
        // TODO: submit the exam instead of the cancelExam()!!
        if (!await this.isSupervisor()) {
          console.log('Submitting exam...');
          await this.warnUserExamSubmitted();
          this.stopExam();
          await this.autoSave.deleteAllSavedFiles();
          await this.authService.logout();
        }

        if (!this.testMode) {
          this.stopExam();
        }
        // this.testMode ? this.stopTest() : this.stopExam();
      }
    }

    // 14/07/2022
    // if result is undefined, then dialog was closed because of the auto submit
    // in that case, do nothing
  }

  private async cancelExam(): Promise<void> {
    this.closeAllDialogs();
    this.cancel();
    this.removeRestrictions(this.activeExam?.featureRestrictions.features.concat(this.featureRestrictionService.defaultBlockedFeatures) ?? undefined);
    this.examTabId = '';
    this.tabService.closeAllTabs();

    // if the information dialog is still open, close it
    // if (this.examInformationDialog) {
    //   this.examInformationDialog.close();
    //   this.examInformationDialog = undefined;
    // }
    // close any house style dialog that might still be open
    this.modalService.closeModal();
  }

  private cancel(): void {
    this.uiStoreService.setItemUnActive(AsgardFeatureNames.EXAM_MODE_FLOATING_TOOLBAR);
    this.exitFullscreen();
    this.timerService.stop();
    this.currentTimeService.stop();
  }

  private setSinglePane(): void {
    if (this.paneService.isDoublePanes) {
      this.paneService.togglePanes();
    }
  }

  private async firstSaveExam(): Promise<void> {
    const docLoadedSub = this.pdfInstanceManager.documentLoaded.subscribe(async () => {
      console.log('document loaded, save exam files');
      await this.examService.saveExamFiles();
      console.log('document loaded, save exam files done');
      docLoadedSub.unsubscribe();
    });
  }

  private async applyRestrictions(restrictedFeatures: AsgardFeatureNames[] | undefined): Promise<void> {
    if (this.testMode) {
      this.testmodeFeatures = [...this.featureRestrictionService.defaultBlockedFeatures];
      if (this.featureRestrictionService.preset) {
        this.testmodeFeatures = this.testmodeFeatures.concat(this.featureRestrictionService.preset.features);
      }

      this.testmodeFeatures.forEach(feature => {
        this.featureRestrictionService.lockFeature(feature);
      });
    } else if (restrictedFeatures !== undefined) {
      restrictedFeatures.forEach(feature => this.featureRestrictionService.lockFeature(feature));
    }

    // block the side panel for pdf
    // can't just do 'getViewerInstance' here because 'setTab()' and 'instanceStorage.set' hasn't happened yet
    // can't be in pdf instance manager because of 'circular dependencies'
    const docLoadedSub = this.pdfInstanceManager.documentLoaded.subscribe(docId => {
      const instance = this.pdfInstanceManager.getViewerInstance(docId);
      instance?.UI.disableElements(['leftPanel']);
      docLoadedSub.unsubscribe();
    });
  }

  private removeRestrictions(restrictedFeatures?: AsgardFeatureNames[] | undefined): void {
    if (this.testMode) {
      this.testmodeFeatures.forEach(feature => {
        this.featureRestrictionService.unlockFeature(feature);
      });
    } else if (restrictedFeatures !== undefined) {
      restrictedFeatures.forEach(feature => this.featureRestrictionService.unlockFeature(feature));
    }

    // unblock the side panel for pdf
    // should not be necessary because tab already closed, but just in case
    const docId = this.documentManager.getDocumentById(this.examTabId)?.documentId
                  ?? this.documentManager.getDocumentById(this.testingExamTabId)?.documentId
                  ?? '';
    const instance = this.pdfInstanceManager.getViewerInstance(docId);
    instance?.UI.enableElements(['leftPanel']);
  }

  private closeAllDialogs(): void {
    this.modalService.closeModal();
    this.dialog.closeAll();
  }

  private closeMainMenu() {
    const mainMenu = document.getElementById('dropdownMainMenu');
    if (mainMenu) {
      mainMenu.className = 'has-menu-dropdown';
    }
  }

  private initSunscriptions() {
    this.subscriptions.add(
      this.currentExamService.examMetadataObservable.subscribe(exam => this.activeExam = exam)
    );
    this.handleAppConnectivityChanges();
  }

  private monitorFeaturesDuringExam(): void {
    if (this.featuresReportSub === null) {
      const features = this.featureRestrictionService.featuresThatCanBeBlockedExam;
      let prevState: CurrentUIState;
      this.featuresReportSub = this.uiService.getUIState().subscribe((state) => {
        if (prevState) {
          // filter out only those who got activated/deactivated
          const diff = features.map((f) => {
            if (!prevState[f].active && state[f].active) {
              return { name: f, state: 'activated' }
            } else if (prevState[f].active && !state[f].active) {
              return { name: f, state: 'deactivated' }
            }
          }).filter((f) => f !== undefined);

          // if there is anything different to report
          if (diff.length > 0) {
            this.reportExamService.sendCustomMessageReport(ReportLogLevel.Features, JSON.stringify(diff));
          }
        }
        // rememver this state
        prevState = this.copy(state);
      });
    }
  }

  private copy(obj: any): any {
    return JSON.parse(JSON.stringify(obj))
  }
}
